import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { HelpCircle, X } from "lucide-react";
import { useTranslation } from "react-i18next";

interface InfoHelperProps {
    titleKey: string;
    descriptionKey: string;
    iconSize?: number;
    titleValues?: Record<string, string | number>;
    descriptionValues?: Record<string, string | number>;
}

const POPOVER_WIDTH = 280;
const POPOVER_MARGIN = 12;

const InfoHelper: React.FC<InfoHelperProps> = ({ titleKey, descriptionKey, iconSize = 14, titleValues, descriptionValues }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState<{ top: number; left: number; above: boolean }>({ top: 0, left: 0, above: false });
    const iconRef = useRef<HTMLButtonElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    const calculatePosition = useCallback(() => {
        if (!iconRef.current) return;

        const rect = iconRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        // Estimate popover height (title + description + padding)
        const estimatedHeight = 120;
        const spaceBelow = viewportHeight - rect.bottom;
        const above = spaceBelow < estimatedHeight + POPOVER_MARGIN;

        const top = above
            ? rect.top - POPOVER_MARGIN
            : rect.bottom + POPOVER_MARGIN;

        // Center horizontally on the icon, clamp to viewport
        let left = rect.left + rect.width / 2 - POPOVER_WIDTH / 2;
        left = Math.max(POPOVER_MARGIN, Math.min(left, viewportWidth - POPOVER_WIDTH - POPOVER_MARGIN));

        setPosition({ top, left, above });
    }, []);

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isOpen) {
            setIsOpen(false);
        } else {
            calculatePosition();
            setIsOpen(true);
        }
    };

    // Close on click outside
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (e: MouseEvent) => {
            if (
                popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
                iconRef.current && !iconRef.current.contains(e.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        const handleScroll = () => setIsOpen(false);

        document.addEventListener("mousedown", handleClickOutside);
        window.addEventListener("scroll", handleScroll, true);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            window.removeEventListener("scroll", handleScroll, true);
        };
    }, [isOpen]);

    // Recalculate position after render to use actual popover height
    useEffect(() => {
        if (!isOpen || !popoverRef.current || !iconRef.current) return;

        const rect = iconRef.current.getBoundingClientRect();
        const popoverHeight = popoverRef.current.offsetHeight;
        const viewportHeight = window.innerHeight;

        const spaceBelow = viewportHeight - rect.bottom;
        const above = spaceBelow < popoverHeight + POPOVER_MARGIN;

        const top = above
            ? rect.top - popoverHeight - POPOVER_MARGIN
            : rect.bottom + POPOVER_MARGIN;

        setPosition(prev => ({ ...prev, top, above }));
    }, [isOpen]);

    return (
        <>
            <button
                ref={iconRef}
                onClick={handleToggle}
                className="inline-flex items-center justify-center text-[#9CA3AF] dark:text-textTertiary-dark hover:text-[#6B7280] dark:hover:text-textSecondary-dark transition-colors"
                aria-label={t(titleKey, titleValues)}
            >
                <HelpCircle size={iconSize} />
            </button>

            {isOpen && createPortal(
                <div
                    ref={popoverRef}
                    style={{
                        position: "fixed",
                        top: position.top,
                        left: position.left,
                        width: POPOVER_WIDTH,
                        zIndex: 9999,
                    }}
                    className="bg-white dark:bg-card-dark border border-[#EAECF0] dark:border-borderPrimary-dark rounded-xl shadow-lg"
                >
                    <div className="flex items-center justify-between px-4 pt-3 pb-1">
                        <span className="text-[13px] font-semibold text-textPrimary dark:text-textPrimary-dark">
                            {t(titleKey, titleValues)}
                        </span>
                        <button
                            onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
                            className="text-[#9CA3AF] dark:text-textTertiary-dark hover:text-[#6B7280] dark:hover:text-textSecondary-dark transition-colors"
                        >
                            <X size={14} />
                        </button>
                    </div>
                    <div className="px-4 pb-3 pt-1">
                        <p className="text-[12px] leading-[18px] text-[#475467] dark:text-textSecondary-dark">
                            {t(descriptionKey, descriptionValues)}
                        </p>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default InfoHelper;
